/**
 * EditClockedOnModal — admin-only modal to edit any active (or completed)
 * clock-on session: site time_entry, travel_entry, machine_usage, or general_hire.
 *
 * Usage:
 *   <EditClockedOnModal
 *     kind="travel" | "machine" | "hire" | "time"
 *     session={row}            // row from live-status or detail endpoint
 *     onClose={() => ...}
 *     onSaved={() => refresh()}
 *   />
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, X, AlertTriangle } from "lucide-react";
import { Button, Modal, Input, Textarea, Select } from "../shared/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const toIso = (local) => (local ? new Date(local).toISOString() : null);

export const EditClockedOnModal = ({ kind, session, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    if (!session) return;
    // Fetch supporting lists once
    (async () => {
      try {
        const [u, m, j] = await Promise.all([
          axios.get(`${API}/users`),
          axios.get(`${API}/machines`).catch(() => ({ data: [] })),
          axios.get(`${API}/jobs`).catch(() => ({ data: [] })),
        ]);
        setUsers(u.data || []);
        setMachines(m.data || []);
        setJobs((j.data || []).filter((x) => x.status !== "completed"));
      } catch (e) {
        /* noop */
      }
    })();
    // Pre-fill form
    if (kind === "time") {
      setForm({
        clock_in: toLocalInput(session.clock_in),
        clock_out: toLocalInput(session.clock_out),
        job_id: session.job_id || "",
        notes: session.notes || "",
        user_id: session.user_id || "",
      });
    } else if (kind === "travel") {
      setForm({
        clock_in: toLocalInput(session.clock_in),
        clock_out: toLocalInput(session.clock_out),
        starting_point: session.starting_point || "",
        destination: session.destination || session.finishing_point || "",
        vehicle_id: session.vehicle_id || session.machine_id || "",
        starting_km: session.starting_km ?? "",
        ending_km: session.ending_km ?? "",
        travel_role: session.travel_role || "driver",
        notes: session.notes || "",
        incidents: session.incidents || "",
      });
    } else if (kind === "machine") {
      setForm({
        clock_on_time: toLocalInput(session.clock_on_time),
        clock_off_time: toLocalInput(session.clock_off_time),
        operator_id: session.operator_id || "",
        job_id: session.job_id || "",
        starting_hours: session.starting_hours ?? "",
        ending_hours: session.ending_hours ?? "",
        starting_kms: session.starting_kms ?? "",
        ending_kms: session.ending_kms ?? "",
        rate_type: session.rate_type || "hourly",
        client_name: session.client_name || "",
        notes: session.notes || "",
      });
    } else if (kind === "hire") {
      setForm({
        clock_on_time: toLocalInput(session.clock_on_time),
        clock_off_time: toLocalInput(session.clock_off_time),
        operator_id: session.operator_id || "",
        machine_id: session.machine_id || "",
        starting_km: session.starting_km ?? "",
        ending_km: session.ending_km ?? "",
        starting_hours: session.starting_hours ?? "",
        ending_hours: session.ending_hours ?? "",
        client_name: session.client_name || "",
        location: session.location || "",
        notes: session.notes || "",
      });
    }
  }, [kind, session]);

  const save = async () => {
    if (!session) return;
    setSaving(true);
    try {
      const id = session.id || session.travel_entry_id || session.machine_usage_id || session.general_hire_id;
      let url, payload;
      if (kind === "time") {
        url = `${API}/time/entries/${id}`;
        payload = {
          clock_in: form.clock_in ? toIso(form.clock_in) : null,
          clock_out: form.clock_out ? toIso(form.clock_out) : null,
          job_id: form.job_id || null,
          notes: form.notes,
          user_id: form.user_id || null,
        };
      } else if (kind === "travel") {
        url = `${API}/travel/entries/${id}`;
        payload = {
          clock_in: form.clock_in ? toIso(form.clock_in) : null,
          clock_out: form.clock_out ? toIso(form.clock_out) : null,
          starting_point: form.starting_point,
          destination: form.destination,
          vehicle_id: form.vehicle_id || null,
          starting_km: form.starting_km === "" ? null : parseFloat(form.starting_km),
          ending_km: form.ending_km === "" ? null : parseFloat(form.ending_km),
          travel_role: form.travel_role,
          notes: form.notes,
          incidents: form.incidents,
        };
      } else if (kind === "machine") {
        url = `${API}/machines/usage/${id}`;
        payload = {
          clock_on_time: form.clock_on_time ? toIso(form.clock_on_time) : null,
          clock_off_time: form.clock_off_time ? toIso(form.clock_off_time) : null,
          operator_id: form.operator_id || null,
          job_id: form.job_id || null,
          starting_hours: form.starting_hours === "" ? null : parseFloat(form.starting_hours),
          ending_hours: form.ending_hours === "" ? null : parseFloat(form.ending_hours),
          starting_kms: form.starting_kms === "" ? null : parseFloat(form.starting_kms),
          ending_kms: form.ending_kms === "" ? null : parseFloat(form.ending_kms),
          rate_type: form.rate_type,
          client_name: form.client_name,
          notes: form.notes,
        };
      } else {
        url = `${API}/general-hires/${id}/admin`;
        payload = {
          clock_on_time: form.clock_on_time ? toIso(form.clock_on_time) : null,
          clock_off_time: form.clock_off_time ? toIso(form.clock_off_time) : null,
          operator_id: form.operator_id || null,
          machine_id: form.machine_id || null,
          starting_km: form.starting_km === "" ? null : parseFloat(form.starting_km),
          ending_km: form.ending_km === "" ? null : parseFloat(form.ending_km),
          starting_hours: form.starting_hours === "" ? null : parseFloat(form.starting_hours),
          ending_hours: form.ending_hours === "" ? null : parseFloat(form.ending_hours),
          client_name: form.client_name,
          location: form.location,
          notes: form.notes,
        };
      }
      // Strip undefined/empty-string keys
      Object.keys(payload).forEach((k) => {
        if (payload[k] === undefined) delete payload[k];
      });
      const method = kind === "time" || kind === "travel" || kind === "machine" ? "patch" : "patch";
      await axios({ method, url, data: payload });
      toast.success("Session updated");
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update session");
    } finally {
      setSaving(false);
    }
  };

  if (!session) return null;

  const userOpts = [
    { value: "", label: "— Select —" },
    ...users.map((u) => ({ value: u.id, label: `${u.first_name} ${u.last_name} (${u.role})` })),
  ];
  const machineOpts = [
    { value: "", label: "— Select —" },
    ...machines.map((m) => ({ value: m.id, label: `${m.name}${m.rego ? ` (${m.rego})` : ""}` })),
  ];
  const jobOpts = [
    { value: "", label: "— No job —" },
    ...jobs.map((j) => ({ value: j.id, label: `${j.title} — ${j.client_name || ""}` })),
  ];

  const titles = {
    time: "Edit Site Clock-On",
    travel: "Edit Travel Session",
    machine: "Edit Machine Usage",
    hire: "Edit General Hire Session",
  };

  return (
    <Modal isOpen onClose={onClose} title={titles[kind] || "Edit Session"} size="lg">
      <div className="space-y-3" data-testid={`edit-${kind}-modal`}>
        <div className="bg-amber-50 border border-amber-200 rounded p-3 flex gap-2 items-start text-sm">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <span className="text-amber-800">
            Any change here is logged with your name/time for audit. Clocking a session off here will also compute <strong>total_hours</strong>.
          </span>
        </div>

        {kind === "time" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Input type="datetime-local" label="Clock In" value={form.clock_in} onChange={(e) => setForm({ ...form, clock_in: e.target.value })} testId="edit-clock-in" />
              <Input type="datetime-local" label="Clock Out (leave blank = still on)" value={form.clock_out} onChange={(e) => setForm({ ...form, clock_out: e.target.value })} testId="edit-clock-out" />
            </div>
            <Select label="Worker" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} options={userOpts} />
            <Select label="Job" value={form.job_id} onChange={(e) => setForm({ ...form, job_id: e.target.value })} options={jobOpts} />
            <Textarea label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </>
        )}

        {kind === "travel" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Input type="datetime-local" label="Started" value={form.clock_in} onChange={(e) => setForm({ ...form, clock_in: e.target.value })} testId="edit-travel-start" />
              <Input type="datetime-local" label="Finished (leave blank = still travelling)" value={form.clock_out} onChange={(e) => setForm({ ...form, clock_out: e.target.value })} testId="edit-travel-end" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Starting Point" value={form.starting_point} onChange={(e) => setForm({ ...form, starting_point: e.target.value })} />
              <Input label="Destination" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} />
            </div>
            <Select
              label="Travel Role"
              value={form.travel_role}
              onChange={(e) => setForm({ ...form, travel_role: e.target.value })}
              options={[{ value: "driver", label: "Driver" }, { value: "passenger", label: "Passenger" }]}
            />
            <Select label="Vehicle / Machine" value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} options={machineOpts} />
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" step="0.1" label="Starting KM" value={form.starting_km} onChange={(e) => setForm({ ...form, starting_km: e.target.value })} />
              <Input type="number" step="0.1" label="Ending KM" value={form.ending_km} onChange={(e) => setForm({ ...form, ending_km: e.target.value })} />
            </div>
            <Textarea label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            <Textarea label="Incidents" value={form.incidents} onChange={(e) => setForm({ ...form, incidents: e.target.value })} rows={2} />
          </>
        )}

        {kind === "machine" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Input type="datetime-local" label="Clocked On" value={form.clock_on_time} onChange={(e) => setForm({ ...form, clock_on_time: e.target.value })} testId="edit-machine-on" />
              <Input type="datetime-local" label="Clocked Off (leave blank = still on)" value={form.clock_off_time} onChange={(e) => setForm({ ...form, clock_off_time: e.target.value })} testId="edit-machine-off" />
            </div>
            <Select label="Operator" value={form.operator_id} onChange={(e) => setForm({ ...form, operator_id: e.target.value })} options={userOpts} />
            <Select label="Job" value={form.job_id} onChange={(e) => setForm({ ...form, job_id: e.target.value })} options={jobOpts} />
            <Input label="Client" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
            <Select label="Rate Type" value={form.rate_type} onChange={(e) => setForm({ ...form, rate_type: e.target.value })} options={[{ value: "hourly", label: "Hourly" }, { value: "daily", label: "Daily" }, { value: "fixed", label: "Fixed" }]} />
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" step="0.1" label="Start Hours" value={form.starting_hours} onChange={(e) => setForm({ ...form, starting_hours: e.target.value })} />
              <Input type="number" step="0.1" label="End Hours" value={form.ending_hours} onChange={(e) => setForm({ ...form, ending_hours: e.target.value })} />
              <Input type="number" step="0.1" label="Start Kms" value={form.starting_kms} onChange={(e) => setForm({ ...form, starting_kms: e.target.value })} />
              <Input type="number" step="0.1" label="End Kms" value={form.ending_kms} onChange={(e) => setForm({ ...form, ending_kms: e.target.value })} />
            </div>
            <Textarea label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </>
        )}

        {kind === "hire" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Input type="datetime-local" label="Clocked On" value={form.clock_on_time} onChange={(e) => setForm({ ...form, clock_on_time: e.target.value })} testId="edit-hire-on" />
              <Input type="datetime-local" label="Clocked Off (leave blank = still on)" value={form.clock_off_time} onChange={(e) => setForm({ ...form, clock_off_time: e.target.value })} testId="edit-hire-off" />
            </div>
            <Select label="Operator" value={form.operator_id} onChange={(e) => setForm({ ...form, operator_id: e.target.value })} options={userOpts} />
            <Select label="Machine" value={form.machine_id} onChange={(e) => setForm({ ...form, machine_id: e.target.value })} options={machineOpts} />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Client" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
              <Input label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" step="0.1" label="Start Hours" value={form.starting_hours} onChange={(e) => setForm({ ...form, starting_hours: e.target.value })} />
              <Input type="number" step="0.1" label="End Hours" value={form.ending_hours} onChange={(e) => setForm({ ...form, ending_hours: e.target.value })} />
              <Input type="number" step="0.1" label="Start KM" value={form.starting_km} onChange={(e) => setForm({ ...form, starting_km: e.target.value })} />
              <Input type="number" step="0.1" label="End KM" value={form.ending_km} onChange={(e) => setForm({ ...form, ending_km: e.target.value })} />
            </div>
            <Textarea label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="edit-save-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditClockedOnModal;
